import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {Config, ConfigService} from "../../config";
import {CountrySettingsDto} from "../../dto/country-settings-dto";
import {GarageSearch, toHttpParams} from "../../../features/products/appointment/models/garage-search";
import {AppointmentInfo} from "../../../features/products/appointment/models/appointment-info";
import {NearestGarages} from "../../../features/products/appointment/models/nearest-garages";
import {Schedule} from "../../../features/products/appointment/models/time-slots";
import {
  CreateAppointmentDto,
  CreateAppointmentResponse
} from "../../../features/products/appointment/models/create-appointment-dto";
import {Utils} from "../../../shared/utils/generics.utils";
import {ProviderServices} from "../../../features/products/appointment/models/provider-services";
import {UiUtils} from "../../../shared/utils/ui.utils";
import {CompanyResponseDto} from "../../dto/company-response-dto";
import {catchError, throwError} from "rxjs";
import {Context} from "../../models/context";
import {GetVehicleFromPictureResponseDto} from "../../dto/get-vehicle-from-picture-response-dto";

@Injectable({
  providedIn: 'root'
})
export class ExternalApiService {

  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private config = this.configService.getConfig().rool

  setConfig(config: Config) {
    this.config = config.rool;
  }

  getCompany(company: string, language: string) {
    const params = new HttpParams({fromObject: {language}});
    return this.http.get<CompanyResponseDto>(`${this.config?.externalApi.url}/v1/web-app/companies/${company}`, {params})
      .pipe(
        catchError((err) => {
          if (err.status === 404 && company.includes('-')) {
            const fallbackCompany = company.split('-')[0];
            return this.http.get<CompanyResponseDto>(`${this.config?.externalApi.url}/v1/web-app/companies/${fallbackCompany}`, {params})
          }
          return throwError(() => err);
        })
      );
  }

  getCountrySettings(countryCode: string) {
    return this.http.get<CountrySettingsDto>(`${this.config.externalApi.url}/v1/countries/${countryCode}/settings`);
  }

  getAppointmentInfo(context: string) {
    return this.http.get<AppointmentInfo>(`${this.config.externalApi.url}/v1/appointments`, {
      params: new HttpParams({fromObject: {context}})
    });
  }

  getNearestGarages(dto: GarageSearch) {
    return this.http.get<NearestGarages>(`${this.config.externalApi.url}/v1/garages/nearest`, {
      params: new HttpParams({fromObject: toHttpParams(dto)})
    });
  }

  createAppointment(dto: CreateAppointmentDto) {
    return this.http.post<CreateAppointmentResponse>(`${this.config.externalApi.url}/v1/appointments`, dto);
  }

  getTimeSlots(garageId: number, serviceId: number, week: number) {
    return this.http.get<Schedule>(`${this.config.externalApi.url}/v1/garages/${garageId}/services/${serviceId}/timeslots`, {
      params: new HttpParams({fromObject: {week}})
    });
  }

  getProviderServices(context: string, provider: string = Utils.getCompanyFromDomain()) {
    return this.http.get<ProviderServices>(`${this.config.externalApi.url}/v1/providers/euromaster/services`, {
      params: new HttpParams({fromObject: {context, language: UiUtils.getBrowserLanguage()}})
    });
    // return this.http.get<ProviderServices>(`${this.config.externalApi.url}/v1/providers/${provider}/services`, {
    //   params: new HttpParams({fromObject: { context }})
    // });
  }

  createAnalysisFromLicensePlatePicture(
    companyKey: string,
    fleet: string,
    pictureFile: File
  ) {
    const formData = new FormData();

    const params = {
      companyKey,
      contextOrigin: fleet
    };

    formData.append('licensePlatePicture', pictureFile);
    formData.append('createVehicleFromPictureParams', new Blob(
      [JSON.stringify(params)],
      {type: 'application/json'}
    ));

    return this.http.post<GetVehicleFromPictureResponseDto>(
      `${this.config.externalApi.url}/v1/web-app/vehicles/by-picture`,
      formData
    );
  }
}
